import React from 'react';
import cx from 'classnames';
import { useQuery } from '@apollo/client';

import {
  ModuleHeader,
  UpsellBanner,
  usePageContext,
  Carousel,
} from '@xingternal/pages-common';
import {
  SkeletonProfileImage,
  SkeletonHeadline,
  SkeletonBody,
} from '@xingternal/skeleton';
import { Card } from '@xingternal/card';
import Error from '@xingternal/pages-warning';

import CompanyRecommendationCard from './CompanyRecommendationCard';
import { CompanyRecommendationsQuery } from '../types';
import COMPANY_RECOMMENDATIONS_QUERY from './graphql/CompanyRecommendations.graphql';
import styles from './CompanyRecommendations.css';

type Props = {
  companyId: string;
};

const CompanyRecommendationCardSkeleton = () => (
  <Card className={styles.card}>
    <div className={styles.profileBackground}>
      <div className={cx(styles.profileImage, styles.skeletonProfileImage)}>
        <SkeletonProfileImage variant="primary" size="large" />
      </div>
    </div>
    <div className={styles.skeletonHeadline}>
      <SkeletonHeadline variant="secondary" size="medium" amountRows={1} />
    </div>
    <SkeletonBody variant="primary" size="medium" amountRows={2} />
  </Card>
);

const CompanyRecommendationCarouselSkeleton = () => (
  <Carousel>
    <CompanyRecommendationCardSkeleton />
    <CompanyRecommendationCardSkeleton />
    <CompanyRecommendationCardSkeleton />
  </Carousel>
);

const CompanyRecommendations = ({ companyId }: Props) => {
  const { pageContext } = usePageContext();
  const { data, loading, refetch } = useQuery<CompanyRecommendationsQuery>(
    COMPANY_RECOMMENDATIONS_QUERY,
    {
      variables: {
        id: companyId,
      },
      errorPolicy: 'all',
    }
  );

  if (!loading && !data?.company?.relatedCompanies) {
    return (
      <div data-testid="errorContainer">
        <ModuleHeader headlineCopyKey="CP_RECOMMENDS_ADVERTISEMENT_ABOUT_US" />
        <Error
          headerText="EP_ERROR_HEADER"
          bodyText="EP_ERROR_BODY"
          buttonText="EP_ERROR_RELOAD_CTA"
          onClick={() => refetch()}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="loadingContainer">
        <ModuleHeader headlineCopyKey="CP_RECOMMENDS_ADVERTISEMENT_ABOUT_US" />
        <CompanyRecommendationCarouselSkeleton />
      </div>
    );
  }

  const upsellActive = pageContext?.upsellActive;

  const recommendedCompanies = data?.company?.relatedCompanies?.collection;

  if (recommendedCompanies?.length === 0) return null;

  return (
    <div data-testid={'COMPANY_RECOMMENDATIONS_MODULE'}>
      <ModuleHeader headlineCopyKey="CP_RECOMMENDS_ADVERTISEMENT_ABOUT_US" />
      {upsellActive ? (
        <div className={styles.upsellLayout}>
          <UpsellBanner
            headerCopyKey="EP_UPSELL_COMPANY_RECOMMENDED_HEADER"
            bodyCopyKey="EP_UPSELL_COMPANY_RECOMMENDED_BODY"
            moduleName="companyrecommendationsmodule"
          />
        </div>
      ) : null}
      <Carousel>
        {recommendedCompanies?.map(
          ({
            company: { id, companyName, kununuData, logos, links, entityPage },
          }) => (
            <CompanyRecommendationCard
              key={id}
              profileImage={logos?.logo128px}
              profileName={companyName}
              numberOfRatings={kununuData?.ratingCount}
              kununuRating={kununuData?.ratingAverage}
              link={links?.public}
              backgroundImageUrl={entityPage?.coverImage?.[0]?.url}
            />
          )
        )}
      </Carousel>
    </div>
  );
};

export default CompanyRecommendations;
